/**
 * ReceivingBankAccountPageList — CRUD de contas de recebimento (billing).
 */
import { useState, useMemo } from 'react';
import PageList from '../../templates/PageList';
import appConfig from '../../../config/app-config';
import ReceivingBankAccountFormModal from './modals/ReceivingBankAccountFormModal';
import { formatDateBR } from '../../../utils/DateUtils';
import { FaEdit } from 'react-icons/fa';

const ReceivingBankAccountPageList = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(100);
    const [refreshKey, setRefreshKey] = useState(0);
    const [formAccount, setFormAccount] = useState<any | undefined>(undefined); // undefined=fechado, null=novo, obj=editar

    const { address: API_URL, key: API_KEY } = appConfig.apis.billing;

    const url = useMemo(
        () => `${API_URL}/receiving-bank-accounts?page=${currentPage + 1}&size=${currentPageSize}&sort=alias_name&_r=${refreshKey}`,
        [API_URL, currentPage, currentPageSize, refreshKey],
    );

    const fetchHeaders = useMemo(() => [{ key: 'x-api-key', value: API_KEY || '' }], [API_KEY]);

    const tableHeaders = useMemo(() => [
        { id: 'aliasName', title: 'Apelido', canSort: false },
        { id: 'exibitionName', title: 'Nome de exibição', canSort: false },
        { id: 'bankName', title: 'Banco', canSort: false },
        // ao lado do Banco: os dois falam da instituição, um por extenso e outro em chave
        { id: 'bankTag', title: 'Tag', canSort: false },
        { id: 'active', title: 'Situação', canSort: false },
        { id: 'createdAt', title: 'Criado em', canSort: false },
        { id: 'actions', title: 'Ações', canSort: false },
    ], []);

    // Não há exclusão: a conta é referenciada por pagamentos já registrados.
    // Ativar/desativar é no modal de edição (toggle), não na linha da listagem.
    const renderRows = (rows: any[]) => rows.map((row: any) => (
        <tr key={row.id} className={`hover:bg-gray-50 ${row.active ? '' : 'opacity-60'}`}>
            <td className="px-4 py-2 text-sm font-mono">{row.aliasName}</td>
            <td className="px-4 py-2 text-sm font-medium">{row.exibitionName}</td>
            <td className="px-4 py-2 text-sm">{row.bankName || '-'}</td>
            <td className="px-4 py-2 text-sm font-mono">{row.bankTag || '-'}</td>
            <td className="px-4 py-2 text-sm">
                <span className={`inline-block text-xs px-2 py-0.5 rounded ${row.active
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'}`}>
                    {row.active ? 'Ativa' : 'Desativada'}
                </span>
            </td>
            <td className="px-4 py-2 text-sm">{formatDateBR(row.createdAt)}</td>
            <td className="px-4 py-2 text-sm whitespace-nowrap">
                <button className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300"
                    onClick={() => setFormAccount(row)} title="Editar"><FaEdit /></button>
            </td>
        </tr>
    ));

    return (
        <>
            <PageList
                pageTitle="Cobrança — Contas de Recebimento"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[
                    { label: 'Home', url: '/home' },
                    { label: 'Financeiro', url: '#' },
                    { label: 'Contas de Recebimento', url: '/billing/receiving-bank-accounts' },
                ]}
                tableHeaders={tableHeaders}
                defaultBackendHeader={false}
                tableFieldSort={'alias_name'}
                tableOrderSort={'ASC'}
                onTableSort={() => { }}
                renderRows={renderRows}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={(p: number) => setCurrentPage(p)}
                onItemsPerPageChange={(s: number) => { setCurrentPageSize(s); setCurrentPage(0); }}
                itemsLabel="contas"
            >
                {{
                    filterSlot: (<div></div>),
                    controlSlot: (
                        <div className="pt-4">
                            <button onClick={() => setFormAccount(null)}
                                className="text-white bg-blue-600 hover:bg-blue-700 font-medium rounded text-sm px-4 py-2">
                                Nova conta
                            </button>
                        </div>
                    ),
                }}
            </PageList>
            {formAccount !== undefined && (
                <ReceivingBankAccountFormModal
                    account={formAccount}
                    onClose={() => setFormAccount(undefined)}
                    onSuccess={() => setRefreshKey(k => k + 1)}
                />
            )}
        </>
    );
};

export default ReceivingBankAccountPageList;
